import useI18n from '@/hooks/useI18n';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomProgressBar from '@/components/common/CustomProgressBar';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';
import { getUsedPlatforms } from '@/utils/socialplatformUtils';
import Loading from '@/components/common/Loading';
import { formatCampaignDate, formatTimeFromISO } from '@/utils/dateUtils';
import { formatPercent, formatMoney } from '@/utils/numberUtils';
import Model from '@/components/common/models/Model';
import LinkShareCard from '@/components/LinkShareCard/LinkShare';
import { isValid } from 'date-fns';
import { BrandMemberRole } from '@/types/enums';
import { UserContext } from '@/context/UserContext';
import ToolTip from '@/components/common/ToolTip';
import { generateSmartLink } from '@/utils/smartLinkUtils';
import { toCampaignShareLink } from '@/utils/campaignShareLinkUtils';
import {
  useGetBrandCampaignsInfiniteQuery,
  useDeleteDraftCampaignMutation,
} from '@/hooks/queries/useCampaignQueries';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/queries/queryKeys';

const DraftCampaign = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [copyLink, setCopyLink] = useState('');
  const userContext = useContext(UserContext);
  const queryClient = useQueryClient();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null,
  );

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;

  const brandId = user?.brand?._id || '';

  const isRegularBrandUser = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.User,
    [user?.brandMemberRole],
  );

  const EmptyState: React.FC<{
    title: string;
    description: string;
    icon?: string;
  }> = ({ title, description, icon = '../assets/paper.png' }) => {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <img src={icon} className="h-12 w-12 opacity-50" />
        <h3 className="text-lg font-medium text-gray-700">{title}</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          {description}
        </p>
      </div>
    );
  };

  const handleShareModalClose = () => setIsShareModalOpen(false);

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const {
    data: campaignsData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useGetBrandCampaignsInfiniteQuery('draft');

  const draftCampaigns =
    campaignsData?.pages.flatMap((page: any) => page?.campaigns ?? []) ?? [];

  const deleteMutation = useDeleteDraftCampaignMutation();

  const handleShareModalOpen = (id: string) => {
    const campaign = draftCampaigns.find((campaign) => campaign._id === id);
    if (campaign && campaign.smartLinkId) {
      const smartLink = generateSmartLink(campaign.smartLinkId);
      setCopyLink(toCampaignShareLink(smartLink));
    } else {
      setCopyLink('');
    }
    setIsShareModalOpen(true);
  };

  const handleDeleteDraftCampaign = async () => {
    if (!selectedCampaignId) return;

    try {
      const response = await deleteMutation.mutateAsync(selectedCampaignId);

      toast.success(response?.message || t('campaign.success.draftDeleted'));
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.byStatusInfinite('draft'),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaigns.stats(brandId),
      });

      setIsDeleteModalOpen(false);
      setSelectedCampaignId(null);
    } catch (error) {
      toast.error(t('campaign.errors.deleteDraftFailed'));
    }
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  return (
    <>
      {!isLoading && draftCampaigns.length <= 0 ? (
        <EmptyState
          title={t('common.noData')}
          description={t('common.noDraftCampaignFound')}
        />
      ) : (
        draftCampaigns.map((campaign) => {
          const rawStartDate = campaign.startDate;
          const rawEndDate = campaign.endDate;

          const startDate = formatCampaignDate(campaign.startDate, false);
          const endDate = formatCampaignDate(campaign.endDate ?? '', false);
          const startTime = formatTimeFromISO(campaign.startDate);
          const platform = getUsedPlatforms(campaign.containType);
          const data = [
            {
              label: t('common.budget'),
              value: isDummyUser
                ? formatMoney(10000)
                : formatMoney(campaign.campaignBudget),
            },
            {
              label: t('common.remainingBalance'),
              value: isDummyUser
                ? formatMoney(7000)
                : formatMoney(campaign.remainingCampaignBudget),
            },
            //TODO : static values , need to update
            {
              label: t('common.participant'),
              value: isDummyUser ? '2k' : campaign.totalParticipants,
            },
            {
              label: t('common.engagement'),
              value: isDummyUser
                ? '20%'
                : formatPercent(campaign.engagementRate),
            },
            // {
            //   label: t('common.earnedMediaValue'),
            //   value: '€10,000(5x)',
            // },
            {
              label: t('common.totalSales'),
              value: isDummyUser
                ? formatMoney(80200)
                : formatMoney(campaign.totalSales ?? 0),
            },
          ];
          return (
            <div
              className="bg-white px-[12px] pt-[12px] sm:px-[24px] sm:pt-[24px] f"
              key={campaign._id}
            >
              <div className="border border-[#E5E9EB] rounded-3xl p-[24px] flex flex-col gap-[24px]">
                <div>
                  <div className="flex justify-between">
                    <span className="text-[#121111] text-lg font-normal capitalize">
                      {campaign?.brandName}
                    </span>
                    <div className="flex gap-4 items-center">
                      {!isRegularBrandUser && (
                        <ToolTip text={String(t('links.edit'))} position="top">
                          <button
                            onClick={() =>
                              navigate(`/edit-campaign/${campaign._id}`)
                            }
                            className="hover:bg-gray-100 rounded-full p-1"
                          >
                            <img
                              src="assets/svgs/fi_edit.svg"
                              className="h-[16px] w-[16px]"
                            />
                          </button>
                        </ToolTip>
                      )}
                      <ToolTip text={String(t('links.share'))} position="top">
                        <button
                          onClick={() => handleShareModalOpen(campaign?._id)}
                          className="hover:bg-gray-100 rounded-full p-1"
                        >
                          <img
                            src="assets/svgs/fi_share.svg"
                            className="h-[16px] w-[16px]"
                          />
                        </button>
                      </ToolTip>
                      <ToolTip text={String(t('links.delete'))} position="top">
                        <button
                          onClick={() => {
                            setSelectedCampaignId(campaign._id);
                            setIsDeleteModalOpen(true);
                          }}
                          className="hover:bg-gray-100 rounded-full p-1"
                        >
                          <img
                            src="assets/svgs/fi_delete.svg"
                            className="h-[16px] w-[16px]"
                          />
                        </button>
                      </ToolTip>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex gap-[8px] items-center">
                      <img
                        src="/assets/fi_calendar.svg"
                        className="h-[16px] w-[16px]"
                      />
                      <span className="text-[#000000B2]">
                        {rawStartDate ? startDate : '~'} -{' '}
                        {campaign?.endDate || !campaign?.runCampaignTillMoneyEnd
                          ? rawEndDate
                            ? endDate
                            : '~'
                          : t('common.untilBudgetEnds')}
                      </span>
                    </div>
                    <div className="flex gap-[8px] items-center">
                      <img
                        src="/assets/fi_clock.svg"
                        className="h-[16px] w-[16px]"
                      />
                      <span className="text-[#000000B2]">
                        {rawStartDate ? startTime : '~'}
                      </span>
                    </div>
                    <div className=" bg-[#FFA5001A] rounded-full px-[10px]">
                      <span className="text-sm font-medium text-[#ff4800]">
                        {t('common.draft')}
                      </span>
                    </div>
                  </div>{' '}
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-[32px]">
                    {data.map((item, index) => (
                      <div key={index}>
                        {item.label === t('common.earnedMediaValue') ? (
                          <div className="text-[#9B919D] font-normal text-sm mb-2">
                            {t('common.earnedMediaValue')}{' '}
                            <ToolTip
                              text={String(t('common.EMVTooltipDesc'))}
                              position="top"
                            >
                              <span className="inline-block">ⓘ</span>
                            </ToolTip>
                          </div>
                        ) : (
                          <div className="text-[#9B919D] font-normal text-sm mb-2">
                            {item.label}
                          </div>
                        )}
                        <div className="flex gap-4 items-center">
                          <div className={`text-lg font-normal text-[#292828]`}>
                            {item.value}
                          </div>
                          {item.label === t('common.remainingBalance') && (
                            <div className="w-[133px]">
                              <CustomProgressBar
                                percentage={
                                  campaign.campaignBudget
                                    ? ((isDummyUser
                                        ? 7000
                                        : campaign.remainingCampaignBudget) /
                                        (isDummyUser
                                          ? 10000
                                          : campaign.campaignBudget)) *
                                      100
                                    : 0
                                }
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-[#9B919D] font-normal text-sm mb-2">
                      {t('common.platform')}
                    </div>
                    <div className="flex gap-4">
                      <div className="flex flex-row gap-3">
                        {platform === 'both' || platform === 'tiktok' ? (
                          <img
                            src="assets/PlateformTikTok.svg"
                            className="w-[24px] h-[24px]"
                          />
                        ) : null}

                        {platform === 'both' || platform === 'instagram' ? (
                          <img
                            src="assets/PlateformInsta.svg"
                            className="w-[24px] h-[24px]"
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
      {/* {isLoading && (
        <div className="flex justify-center items-center py-4">
          <Loading />
        </div>
      )} */}

      {hasNextPage && !isLoading && <div ref={sentinelRef} className="h-1" />}

      {isShareModalOpen && (
        <Model isOpen={isShareModalOpen} onClose={handleShareModalClose}>
          <LinkShareCard
            handleShareModalClose={handleShareModalClose}
            link={copyLink}
          />
        </Model>
      )}
      {isDeleteModalOpen && (
        <Model
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          className="bg-black/30"
        >
          <div className="w-[280px] h-[150px] rounded-2xl flex flex-col justify-between">
            <p className="font-manrope text-[24px] leading-[32px] tracking-normal font-medium text-[#212121]">
              {t('campaign.labels.deleteDraftCampaign')}
            </p>

            <p className="font-manrope text-[14px] leading-[20px] tracking-[0.25px] text-[#212121] font-normal">
              {t('campaign.messages.deleteDraftCampaignMessage')}
            </p>

            <div className="mt-6 flex w-full items-center justify-between gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 h-[40px] rounded-full border border-[#113E53] text-[#113E53]
             hover:bg-[#113E53] hover:text-white hover:border-[#113E53]
             transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>

              <button
                onClick={handleDeleteDraftCampaign}
                className="flex-1 h-[40px] rounded-full bg-[#113E53] text-white"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </Model>
      )}

      {/* {isFetchingNextPage && (
        <div className="">
          <Loading className="!top-[97%] !left-[58.5%]" />
        </div>
      )} */}
    </>
  );
};

export default DraftCampaign;
